import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { flushSync } from 'react-dom';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import type { Organization } from '@/api/organizations';
import { consoleRoutePaths } from '@/navigation/consoleRoutePaths';
import { useSetActingOrg } from '@/stores/authStore';

export type OrganizationWorkspaceDestination = 'devices' | 'people';

const organizationDestinationPath = (destination: OrganizationWorkspaceDestination): string =>
  destination === 'people' ? consoleRoutePaths.users : consoleRoutePaths.devices;

/**
 * Own the only browser transition between platform and organization scope.
 * Every transition cancels and clears cached tenant data before navigation.
 */
export function useConsoleScopeTransition() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setActingOrg = useSetActingOrg();
  const [isSwitchingScope, setIsSwitchingScope] = useState(false);

  const transition = async (
    updateScope: () => void,
    destination: string,
    message: string,
  ): Promise<void> => {
    setIsSwitchingScope(true);
    try {
      await queryClient.cancelQueries();
      // Route guards consume the scope synchronously. Commit it before changing
      // the URL so they cannot redirect using the previous scope render.
      flushSync(updateScope);
      queryClient.clear();
      navigate(destination);
      toast.success(message);
    } finally {
      setIsSwitchingScope(false);
    }
  };

  const enterOrganization = async (
    organization: Organization,
    destination: OrganizationWorkspaceDestination = 'devices',
  ): Promise<void> => {
    await transition(
      () => setActingOrg(organization.id, organization.name),
      organizationDestinationPath(destination),
      `Entered ${organization.name || `Organization ${organization.id}`}`,
    );
  };

  const returnToPlatform = async (): Promise<void> => {
    await transition(
      () => setActingOrg(null, null),
      consoleRoutePaths['owner-organizations'],
      'Returned to platform scope',
    );
  };

  return {
    enterOrganization,
    isSwitchingScope,
    returnToPlatform,
  };
}
